import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_PaginationProps } from '../../../../design/pagination/S_Pagination';
import { EditIcon } from '../../../../design/static/icons/EditIcon';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { enumDirectionToStringOrder, stringOrderToEnumDirection } from '../../../components/table/TableHelpers';
import { EnableFor } from '../../../containers/EnableForRole';
import { SortingDirection } from '../../../models/sortingDirection.model';
import { ITeamMember } from '../models/TeamMember';
import { useTeamPermissions } from '../team.permissions';

interface IProps {
  currentUserEmail: string;
  teamMembers: ITeamMember[];
  onToggleRemoveUserModal(email: string): void;
  onToggleChangeUserRoleModal(email: string): void;
  orderBy: string;
  orderDirection: SortingDirection | undefined;
  onSortChange(orderBy: string, orderDirection: SortingDirection | undefined): Promise<void>;
  pagination: S_PaginationProps;
  isLoading: boolean;
}

export const TeamList = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useTeamPermissions();

  const onRemove = (email: string) => {
    return (): void => {
      props.onToggleRemoveUserModal(email);
    };
  };

  const onEdit = (email: string) => {
    return (): void => {
      props.onToggleChangeUserRoleModal(email);
    };
  };

  const actionsRenderer = (data: ITeamMember): ReactElement => {
    const canManageUser = props.currentUserEmail.toLocaleUpperCase() !== data.email.toLocaleUpperCase() && data.role !== 'Owner';

    return canManageUser ? (
      <>
        <EnableFor hasPermission={permissions.canManageTeamMembers()}>
          <IconButton
            onClick={onEdit(data.email)}
            color='primary'
            size='sm'
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size='sm'
            onClick={onRemove(data.email)}
          >
            <TrashIcon />
          </IconButton>
        </EnableFor>
      </>
    ) : (
      <></>
    );
  };

  const handleSortChange = async (orderBy: string, orderDirection: string): Promise<void> => {
    await props.onSortChange(orderBy, stringOrderToEnumDirection(orderDirection));
  };

  const columns: IGColumnProps<ITeamMember>[] = [
    {
      sortBy: 'email',
      headerName: t('email'),
      renderer: (x) => x.email,
    },
    {
      sortBy: 'name',
      headerName: t('name'),
      renderer: (x) => x.name,
    },
    {
      sortBy: 'role',
      headerName: t('role'),
      renderer: (x) => x.role,
    },
    {
      sortBy: 'submitted',
      headerName: t('submitted'),
      headerTooltip: t('submitted_tooltip'),
      renderer: (x) => x.submitted,
    },
    {
      sortBy: 'reviewed',
      headerName: t('reviewed'),
      headerTooltip: t('reviewed_tooltip'),
      renderer: (x) => x.reviewed,
    },
    {
      sortBy: 'edited',
      headerName: t('edited'),
      headerTooltip: t('edited_tooltip'),
      renderer: (x) => x.edited,
    },
  ];

  if (permissions.canManageTeamMembers().allowed) {
    columns.push({
      actions: true,
      headerName: t('actions'),
      width: 'min-content',
      renderer: actionsRenderer,
    });
  }

  return (
    <GTable
      isLoading={props.isLoading}
      noItemsPlaceholder={t('there_are_no_active_collaborators')}
      columns={columns}
      items={props.teamMembers}
      onSortChanged={handleSortChange}
      sortingModel={[{ orderBy: props.orderBy, orderType: enumDirectionToStringOrder(props.orderDirection) }]}
      paginationProps={props.pagination}
    />
  );
});
